import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, X, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";

const EMPTY = { title: "", department_id: null, summary: "", body: "", lawyer_ids: [], article_ids: [], order: 100, seo_title: "", seo_description: "" };

export default function AdminServices() {
  const { headers } = useAuth();
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [s, d, l, a] = await Promise.all([
        api.get("/admin/services", { headers }),
        api.get("/admin/departments", { headers }),
        api.get("/admin/lawyers", { headers }),
        api.get("/admin/insights", { headers }),
      ]);
      setItems(s.data); setDepartments(d.data); setLawyers(l.data); setArticles(a.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const deptLookup = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);
  const filtered = useMemo(() => {
    return items.filter((s) => {
      if (filter !== "all" && s.department_id !== filter) return false;
      if (q && !s.title.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [items, filter, q]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing.id) await api.put(`/admin/services/${editing.id}`, editing, { headers });
      else await api.post(`/admin/services`, editing, { headers });
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await api.delete(`/admin/services/${id}`, { headers });
      toast.success("Deleted.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleId = (key) => (id) => {
    setEditing((cur) => {
      const arr = cur[key] || [];
      return { ...cur, [key]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
    });
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="overline text-graphite/55">Services</p>
          <h1 className="mt-3 h-display text-4xl md:text-5xl">Manage services <span className="text-graphite/40 text-xl">· {items.length}</span></h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="cec-btn cec-btn-primary" data-testid="admin-add-service">
          <Plus size={16} /> New service
        </button>
      </div>

      <div className="hairline-top hairline-bottom py-4 flex flex-wrap items-center gap-4">
        <span className="overline text-graphite/55">Department</span>
        <select className="edit-input !py-2 !w-auto" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="services-filter-dept">
          <option value="all">All ({items.length})</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-graphite/45" />
          <input className="edit-input !py-2 pl-9" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="services-search" />
        </div>
      </div>

      <div className="hairline-top">
        {filtered.map((s) => (
          <div key={s.id} className="grid grid-cols-12 gap-4 py-4 hairline-bottom items-center" data-testid={`admin-service-${s.id}`}>
            <div className="col-span-6">
              <p className="font-medium">{s.title}</p>
              <p className="text-xs text-graphite/55 mt-0.5 truncate">{s.summary}</p>
            </div>
            <div className="col-span-3 text-sm text-graphite/70">{deptLookup[s.department_id]?.title || <span className="text-graphite/45">No department</span>}</div>
            <div className="col-span-1 text-sm text-graphite/65">{(s.lawyer_ids || []).length} 👤</div>
            <div className="col-span-2 flex justify-end gap-2">
              <button className="p-2 hover:text-[#1E3668]" onClick={() => setEditing({ ...s })} aria-label="Edit" data-testid={`admin-edit-service-${s.id}`}><Pencil size={16} /></button>
              <button className="p-2 hover:text-red-700" onClick={() => del(s.id)} aria-label="Delete"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center py-16 text-graphite/55">No services match.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-graphite/40 flex items-start justify-center px-4 overflow-y-auto py-12" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-warmwhite max-w-4xl w-full p-8 md:p-12 hairline" data-testid="admin-service-form">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline text-graphite/55">{editing.id ? "Edit" : "New"} service</p>
                <h2 className="mt-3 h-display text-3xl">{editing.title || "Untitled"}</h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="p-2"><X size={18} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-8">
              <div className="md:col-span-2"><label className="overline text-graphite/55">Title</label><input className="edit-input" required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              <div>
                <label className="overline text-graphite/55">Department</label>
                <select className="edit-input" value={editing.department_id || ""} onChange={(e) => setEditing({ ...editing, department_id: e.target.value || null })} data-testid="admin-service-dept">
                  <option value="">— Unassigned —</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                </select>
              </div>
              <div>
                <label className="overline text-graphite/55">Display order</label>
                <input type="number" className="edit-input" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) || 0 })} />
              </div>
              <div className="md:col-span-2"><label className="overline text-graphite/55">Summary</label><textarea className="edit-textarea" rows={2} value={editing.summary || ""} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} /></div>
              <div className="md:col-span-2 mt-2">
                <label className="overline text-graphite/55 block mb-2">Body</label>
                <RichTextEditor
                  value={editing.body || ""}
                  onChange={(html) => setEditing({ ...editing, body: html })}
                  placeholder="What this service covers, how we work…"
                  minHeight={260}
                  testId="admin-service-body"
                />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div data-testid="admin-service-lawyers">
                <p className="overline text-graphite/55 mb-3">Related lawyers ({(editing.lawyer_ids || []).length})</p>
                <div className="max-h-64 overflow-y-auto hairline p-3 space-y-1">
                  {lawyers.map((l) => (
                    <label key={l.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:text-[#1E3668]">
                      <input type="checkbox" checked={(editing.lawyer_ids || []).includes(l.id)} onChange={() => toggleId("lawyer_ids")(l.id)} data-testid={`admin-svc-lawyer-${l.id}`} />
                      <span>{l.name}</span>
                      {l.title && <span className="text-graphite/45">· {l.title}</span>}
                    </label>
                  ))}
                </div>
              </div>
              <div data-testid="admin-service-articles">
                <p className="overline text-graphite/55 mb-3">Related articles ({(editing.article_ids || []).length})</p>
                <div className="max-h-64 overflow-y-auto hairline p-3 space-y-1">
                  {articles.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:text-[#1E3668]">
                      <input type="checkbox" checked={(editing.article_ids || []).includes(a.id)} onChange={() => toggleId("article_ids")(a.id)} />
                      <span>{a.title}</span>
                    </label>
                  ))}
                  {articles.length === 0 && <p className="text-xs text-graphite/55">No articles yet.</p>}
                </div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 hairline-top pt-6">
              <div><label className="overline text-graphite/55">SEO title</label><input className="edit-input" value={editing.seo_title || ""} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} /></div>
              <div><label className="overline text-graphite/55">SEO description</label><input className="edit-input" value={editing.seo_description || ""} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} /></div>
            </div>

            <div className="mt-10 flex items-center gap-3">
              <button type="submit" disabled={busy} className="cec-btn cec-btn-primary" data-testid="admin-service-save">{busy ? "Saving…" : "Save"}</button>
              <button type="button" onClick={() => setEditing(null)} className="cec-btn">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
